import React, { useEffect, useState } from 'react'
import {
	Button,
	Container,
	Title,
	Alert,
	PasswordInput,
	Space,
	Text,
	Center,
	Paper,
} from '@mantine/core'
import { AlertCircle } from 'tabler-icons-react'
import { useSearchParams, useNavigate } from 'react-router-dom'

const ResetPasswordPage = () => {
	const [params] = useSearchParams()
	const navigate = useNavigate()

	const token = params.get('token')
	const mode = params.get('mode')

	const [email, setEmail] = useState('')
	const [hasSetPassword, setHasSetPassword] = useState(false)
	const [valid, setValid] = useState(false)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'

	useEffect(() => {
		const checkToken = async () => {
			try {
				const res = await fetch(
					`${BASE_URL}/auth/validate-reset-token?token=${token}`
				)
				const data = await res.json()

				if (!res.ok) throw new Error(data.message)

				setEmail(data.email)
				setHasSetPassword(data.hasSetPassword)
				setValid(true)
			} catch (err) {
				setError(err.message || 'Invalid or expired token.')
			} finally {
				setLoading(false)
			}
		}

		if (token) checkToken()
	}, [token])

	const handleSubmit = async () => {
		if (password !== confirmPassword) {
			setError('Passwords do not match.')
			return
		}

		setSubmitting(true)
		setError(null)

		try {
			const res = await fetch(`${BASE_URL}/auth/reset-password`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ token, password }),
			})

			const data = await res.json()
			if (!res.ok) throw new Error(data.message)

			// Redirect to login
			navigate('/login')
		} catch (err) {
			setError(err.message)
		} finally {
			setSubmitting(false)
		}
	}

	const handleGoogleLogin = () => {
		window.location.href = `${BASE_URL}/auth/google`
	}

	if (loading) return <Text align='center'>Loading...</Text>
	if (!valid)
		return (
			<Alert icon={<AlertCircle />} color='red' title='Invalid Link'>
				{error || 'This reset link is invalid or expired.'}
			</Alert>
		)

	const isNewUser = mode === 'new-user'

	return (
		<Container size='xs'>
			<Center style={{ height: '100vh' }}>
				<Paper withBorder shadow='sm' p='xl' radius='md' w='100%'>
					<Title align='center' order={2}>
						{isNewUser ? 'Activate Your Account' : 'Reset Your Password'}
					</Title>

					<Text align='center' c='dimmed' size='sm'>
						{isNewUser
							? `You're invited as ${email}`
							: `Reset password for ${email}`}
					</Text>

					<Space h='lg' />

					<PasswordInput
						label='New Password'
						value={password}
						onChange={(e) => setPassword(e.target.value)}
					/>
					<PasswordInput
						label='Confirm Password'
						value={confirmPassword}
						onChange={(e) => setConfirmPassword(e.target.value)}
						mt='md'
					/>

					{error && (
						<Alert
							icon={<AlertCircle size={16} />}
							color='red'
							title='Error'
							mt='md'
						>
							{error}
						</Alert>
					)}

					<Space h='md' />
					<Button
						fullWidth
						loading={submitting}
						onClick={handleSubmit}
						disabled={!password || !confirmPassword}
					>
						{isNewUser ? 'Set Password & Activate' : 'Reset Password'}
					</Button>
					{isNewUser && (
						<>
							<Space h='lg' />
							<Text align='center' c='dimmed' size='sm' mb='xs'>
								Or
							</Text>
							<Button
								fullWidth
								variant='outline'
								onClick={handleGoogleLogin}
								mb='sm'
							>
								Continue with Google
							</Button>
						</>
					)}
				</Paper>
			</Center>
		</Container>
	)
}

export default ResetPasswordPage
